import { z } from 'zod'
import { guideTargetSchema } from '../../guide'
import type {
  RecommendationBatch,
  RecommendationCatalog,
} from '../domain/recommendation'
import type { Receipt } from '../domain/story'
import { decisionSchema, mapStory, storySchema } from './engagement-contracts'
const instant = z.iso.datetime({ offset: true }).transform(Date.parse)
const slide = z
  .object({
    reservation_id: z.uuid(),
    recommendation_id: z.uuid(),
    revision_id: z.uuid(),
    expires_at: instant,
    story: storySchema,
    guide_target: guideTargetSchema,
  })
  .transform((v) => ({
    reservationId: v.reservation_id,
    recommendationId: v.recommendation_id,
    revisionId: v.revision_id,
    expiresAt: v.expires_at,
    story: mapStory(v.story),
    guideTarget: v.guide_target,
  }))
export const batchSchema = z
  .object({
    schema_version: z.literal(2),
    batch_id: z.uuid(),
    mode: z.enum(['auto', 'manual']),
    cursor: z.number().int().min(0).max(1000),
    total: z.number().int().min(0).max(1000),
    looping: z.boolean().default(false),
    closed: z.boolean(),
    expires_at: instant,
    current: slide.nullish(),
  })
  .refine((v) =>
    v.looping ? v.mode === 'manual' : v.total <= 3 && v.cursor <= 3,
  )
  .refine((v) => v.current == null || (!v.closed && v.cursor < v.total))
  .transform((v): RecommendationBatch => ({
    id: v.batch_id,
    mode: v.mode,
    looping: v.looping,
    cursor: v.cursor,
    total: v.total,
    closed: v.closed,
    expiresAt: v.expires_at,
    current: v.current ?? null,
  }))
export const unifiedDecisionSchema = z
  .discriminatedUnion('kind', [
    z.object({ schema_version: z.literal(3), kind: z.literal('none') }),
    z.object({
      schema_version: z.literal(3),
      kind: z.literal('marketing'),
      marketing: decisionSchema.refine((v) => v.kind === 'marketing'),
    }),
    z.object({
      schema_version: z.literal(3),
      kind: z.literal('recommendations'),
      recommendations: batchSchema,
    }),
  ])
  .transform((v) =>
    v.kind === 'marketing'
      ? v.marketing
      : v.kind === 'recommendations'
        ? { kind: 'recommendations' as const, batch: v.recommendations }
        : { kind: 'none' as const },
  )
export const recommendationReceiptSchema = z
  .object({
    schema_version: z.literal(1),
    reservation_id: z.uuid(),
    shown_at: instant,
    first_action: z.enum(['details', 'next', 'close']).nullish(),
    ended_at: instant.nullish(),
  })
  .transform((v): Receipt => ({
    reservationId: v.reservation_id,
    shownAt: v.shown_at,
    firstAction:
      v.first_action === 'close'
        ? 'close'
        : v.ended_at != null || v.first_action != null
          ? 'cta'
          : null,
  }))
export const catalogSchema = z
  .object({
    schema_version: z.literal(2),
    total: z.number().int().nonnegative(),
    seen_count: z.number().int().nonnegative(),
    items: z.array(
      z.object({
        recommendation_id: z.uuid(),
        revision_id: z.uuid(),
        guide_target: guideTargetSchema,
        title: z.string().min(1).max(140),
        seen: z.boolean(),
      }),
    ),
  })
  .refine(
    (v) =>
      v.total === v.items.length &&
      v.seen_count === v.items.filter((i) => i.seen).length,
  )
  .transform((v): RecommendationCatalog => ({
    total: v.total,
    seenCount: v.seen_count,
    items: v.items.map((i) => ({
      id: i.recommendation_id,
      revisionId: i.revision_id,
      guideTarget: i.guide_target,
      title: i.title,
      seen: i.seen,
    })),
  }))
